import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { companyInfo } from '../data/mock';
import { SEO } from '../components/SEO';

export default function TermsPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Terms of Service"
        path="/terms"
        description="Terms and conditions governing the use of Naviton Global's website and trade services."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-12 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Terms of Service
              </h1>
              <p className="text-lg text-slate-300">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-slate max-w-none">
              
              <h2 className="text-2xl font-bold text-navy-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                By accessing or using the website and services of {companyInfo.name} ("Company", "we", "us", or "our"), 
                you agree to be bound by these Terms of Service. If you disagree with any part of these terms, 
                you may not access our website or use our services.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">2. Description of Services</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                {companyInfo.name} provides international trade services including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Import and export facilitation</li>
                <li>Domestic trade and distribution within Australia</li>
                <li>International sourcing and supplier matching</li>
                <li>Logistics coordination and freight management</li>
                <li>Trade documentation and compliance support</li>
                <li>Market entry advisory services</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">3. Use of Website</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Use the website in any way that violates applicable laws or regulations</li>
                <li>Attempt to gain unauthorised access to any part of the website</li>
                <li>Use the website to transmit harmful code or malware</li>
                <li>Impersonate any person or entity</li>
                <li>Collect or harvest any information from the website without authorisation</li>
                <li>Interfere with the proper working of the website</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">4. Intellectual Property</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                The website and its entire contents, features, and functionality (including but not limited to 
                all information, software, text, displays, images, video, and audio) are owned by {companyInfo.name} 
                and are protected by Australian and international copyright, trademark, and other intellectual 
                property laws. You may not reproduce, distribute, modify, or create derivative works without 
                our express written permission.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">5. User Submissions</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                When you submit enquiries, messages, or other content through our website, you grant us 
                a non-exclusive, royalty-free licence to use, reproduce, and process such submissions 
                for the purpose of providing our services and responding to your requests. You represent 
                that you have the right to submit such content and that it does not violate any third-party rights.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">6. Trade Services Terms</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                For trade-related services, additional terms may apply:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>All trade transactions are subject to separate commercial agreements</li>
                <li>Pricing, delivery, and payment terms will be specified in individual quotes or contracts</li>
                <li>We act as facilitators and intermediaries; final responsibility for goods lies with buyers and sellers</li>
                <li>All services are subject to applicable trade laws and regulations</li>
                <li>We reserve the right to refuse service for any reason</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">7. FuelIQ Application</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                FuelIQ is a digital venture operated by {companyInfo.name} — a smart fuel-price
                comparison and savings platform for Australian drivers. By downloading, accessing, or
                using FuelIQ, you agree to the following additional terms:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>You may sign in using Google Sign-In. You are responsible for maintaining the
                  confidentiality of your account and for all activity that occurs under it.</li>
                <li>Fuel prices, station locations, and savings estimates are provided for general
                  informational purposes only and may not always be accurate, complete, or current.
                  You should confirm prices at the point of sale before making a purchase.</li>
                <li>FuelIQ does not sell fuel and is not responsible for the products, pricing, or
                  services of any fuel retailer displayed within the app.</li>
                <li>Location features rely on your device permissions and third-party data sources;
                  availability and accuracy may vary by area.</li>
                <li>You agree to use FuelIQ only for lawful, personal, non-commercial purposes and not
                  to scrape, resell, or misuse its data.</li>
                <li>Your use of FuelIQ is also governed by our Privacy Policy, which explains how we
                  handle information collected through the application.</li>
              </ul>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">8. Disclaimer of Warranties</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                The website and services are provided on an "as is" and "as available" basis. We make no 
                warranties, express or implied, regarding the website's operation, availability, accuracy, 
                or the results that may be obtained from its use. To the fullest extent permitted by law, 
                we disclaim all warranties, including implied warranties of merchantability, fitness for a 
                particular purpose, and non-infringement.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">9. Limitation of Liability</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                To the maximum extent permitted by Australian law, {companyInfo.name} shall not be liable for 
                any indirect, incidental, special, consequential, or punitive damages, including but not 
                limited to loss of profits, data, or business opportunities, arising out of or related to 
                your use of our website or services. Our total liability shall not exceed the amount you 
                paid us, if any, for services during the twelve (12) months preceding the claim.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">10. Indemnification</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                You agree to indemnify and hold harmless {companyInfo.name}, its officers, directors, employees, 
                and agents from any claims, damages, losses, or expenses (including legal fees) arising from 
                your use of the website, violation of these Terms, or infringement of any third-party rights.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">11. Governing Law</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of New South Wales, 
                Australia. Any disputes arising from these Terms or your use of our services shall be subject 
                to the exclusive jurisdiction of the courts of New South Wales.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">12. Changes to Terms</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately 
                upon posting to the website. Your continued use of the website after changes constitutes 
                acceptance of the modified Terms. We encourage you to review these Terms periodically.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">13. Severability</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall 
                be limited or eliminated to the minimum extent necessary, and the remaining provisions shall 
                remain in full force and effect.
              </p>

              <h2 className="text-2xl font-bold text-navy-900 mb-4 mt-8">14. Contact Information</h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                For any questions regarding these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <p className="text-slate-700 font-semibold">{companyInfo.name}</p>
                <p className="text-slate-600">Email: info@navitonglobal.com.au</p>
                <p className="text-slate-600">Phone: +61 478822581</p>
                <p className="text-slate-600">Address: Sydney, Australia</p>
                <p className="text-slate-600">ABN: {companyInfo.abn}</p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
